import project14pxIcon from '@jetbrains/icons/project-14px.svg'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import type {WrapTextProps} from '@jetbrains/ring-ui/components/link/link'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import classNames from 'classnames'
import * as React from 'react'
import type {ComponentType} from 'react'

import {getProjectHref} from '../../../../../routes'
import {toProjectId} from '../../../../../types'
import Popup from '../../../../common/Popup/Popup.lazy'
import RouterLink from '../../../../common/RouterLink/RouterLink'
import SvgIcon from '../../../../common/SvgIcon/SvgIcon'
import MatchingTitle from '../../components/MatchingItem/MatchingItem'
import type {AgentPoolProjectsTreeProjectItem} from '../AgentPoolPage.types'

import type {PathProps, PathItemContainerProps, ItemComponentProps} from './ProjectPath.types'

import styles from './ProjectPath.css'

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation()

type ProjectContentProps = ItemComponentProps & {
  WrapText?: ComponentType<WrapTextProps>
}

function ProjectContent({
  searchQuery,
  projectItem,
  WrapText = React.Fragment,
}: ProjectContentProps) {
  const {name, matching} = projectItem
  return (
    <>
      <span className={styles.icon}>
        <SvgIcon icon={project14pxIcon} />
      </span>
      <WrapText>
        {matching ? <MatchingTitle searchQuery={searchQuery}>{name}</MatchingTitle> : name}
      </WrapText>
    </>
  )
}

export function ProjectLine({
  projectItem,
  searchQuery,
  className,
}: ItemComponentProps): React.ReactElement {
  return (
    <span className={className} title={projectItem.name}>
      <ProjectContent projectItem={projectItem} searchQuery={searchQuery} />
    </span>
  )
}

function ProjectLink({
  projectItem,
  searchQuery,
  className,
}: ItemComponentProps): React.ReactElement {
  return (
    <RouterLink
      onClick={stopPropagation}
      to={getProjectHref(toProjectId(projectItem.rawId))}
      className={classNames(styles.link, className)}
      title={projectItem.name}
    >
      {WrapText => (
        <ProjectContent projectItem={projectItem} searchQuery={searchQuery} WrapText={WrapText} />
      )}
    </RouterLink>
  )
}

function PathItemContainer({
  projectItem,
  parentItems,
  searchQuery,
  className,
  itemClassName,
  itemComponent: ItemComponent = ProjectLink,
}: PathItemContainerProps) {
  return (
    <div className={classNames(className, styles.pathItem)}>
      {parentItems.length > 0 ? (
        <Dropdown
          className={styles.parentsDropDownAnchor}
          anchor={<span className={styles.ellipsis}>{'...'}</span>}
          hoverShowTimeOut={300}
          hoverHideTimeOut={300}
          clickMode={false}
          hoverMode
        >
          <Popup left={-1} directions={[Directions.TOP_RIGHT, Directions.BOTTOM_RIGHT]}>
            <div className={styles.parentsPopup}>
              {parentItems.map((parentItem, i) => (
                <React.Fragment key={parentItem.rawId}>
                  <ItemComponent projectItem={parentItem} searchQuery={searchQuery} />
                  {i < parentItems.length - 1 ? (
                    <span className={styles.separator}>{'/'}</span>
                  ) : null}
                </React.Fragment>
              ))}
            </div>
          </Popup>
        </Dropdown>
      ) : null}
      {parentItems.length > 0 ? <span className={styles.separator}>{'/'}</span> : null}
      <ItemComponent
        projectItem={projectItem}
        searchQuery={searchQuery}
        className={itemClassName}
      />
    </div>
  )
}

export default function ProjectPath({
  parentItems,
  projectItem,
  searchQuery,
  itemContainerClassName,
  itemClassName,
  itemComponent,
  className,
  style,
  onClick,
}: PathProps): React.ReactElement | null {
  const accumulatedParentItems: Array<AgentPoolProjectsTreeProjectItem> = []
  return (
    <span
      className={classNames(styles.path, className)}
      style={style}
      onClick={onClick}
      role={'presentation'}
    >
      <PathItemContainer
        projectItem={projectItem}
        searchQuery={searchQuery}
        className={classNames(itemContainerClassName, styles.targetItem)}
        itemClassName={itemClassName}
        parentItems={parentItems}
        itemComponent={itemComponent}
      />
      {parentItems
        .map(parentItem => {
          const pathItem = (
            <PathItemContainer
              projectItem={parentItem}
              parentItems={[...accumulatedParentItems]}
              key={parentItem.rawId}
              searchQuery={searchQuery}
              className={classNames(itemContainerClassName, styles.parentItemContainer)}
              itemClassName={classNames(styles.parentItem, itemClassName)}
              itemComponent={itemComponent}
            />
          )

          accumulatedParentItems.push(parentItem)

          return pathItem
        })
        .reverse()}
    </span>
  )
}
